export const RELOAD_GUARD_PREFIX = 'armani:reload-guard='

const BASE_BLOCK_MS = 30_000
const MAX_BLOCK_MS = 5 * 60_000

/**
 * Loop guard for the stale-build reloads in
 * `plugins/00.handleChunkErrors.client.ts`.
 * Nuxt's reloadNuxtApp's built-in guard is 10s, too short.
 *
 * 1. Blocks a reload attempt for the same page inside a 30s time window, to avoid triggering the same reload multiple times.
 * 2. If the reload does not fix the problem for the page in question, the reload is retried with a larger window (doubles each attempt, capped at 5m).
 *    Larger window > more time to allow the root problem to resolve
 * 3. Attempts, time window and current page path are stored in window.name, the `guard`.
 *
 *
 * The guard travels as a prefixed JSON string in `window.name`, e.g.:
 * `armani:reload-guard={"path":"/en-us/cart/","attempts":2,"blockedUntil":1756312345678}`
 * — meaning: `/en-us/cart/` has been reloaded twice; a third reload is
 * blocked until the timestamp (1 minute after the second one).
 *
 * @returns { allowed: boolean, guard: string } `allowed` tracks if the reload is permitted, `guard` is the guard settings for the next attempt.
 */
export function decideReload(
  persisted: string,
  path: string,
  now: number
): { allowed: boolean; guard: string } {
  let previousReloadAttempt: {
    path?: string
    attempts?: number
    blockedUntil?: number
  } = {}
  if (persisted.startsWith(RELOAD_GUARD_PREFIX)) {
    try {
      previousReloadAttempt = JSON.parse(
        persisted.slice(RELOAD_GUARD_PREFIX.length)
      )
    } catch {
      // malformed guard: treat as absent
    }
  }

  // RELOAD ATTEMPT REJECTED: block app reload attempts inside the throttling window on the same path
  const samePath = previousReloadAttempt.path === path
  const isThrottledReload = now < (previousReloadAttempt.blockedUntil ?? 0)
  if (samePath && isThrottledReload) {
    return { allowed: false, guard: persisted }
  }

  // RELOAD ATTEMPT APPROVED: create a new guard for the next attempt
  const attempts = samePath ? (previousReloadAttempt.attempts ?? 0) + 1 : 1
  const blockedUntil =
    now + Math.min(BASE_BLOCK_MS * 2 ** (attempts - 1), MAX_BLOCK_MS)
  return {
    allowed: true,
    guard:
      RELOAD_GUARD_PREFIX + JSON.stringify({ path, attempts, blockedUntil }),
  }
}
